import type { FetchParams, PaginatedResponse } from "@nuxt/app";
import HttpFactory from "~/repository/factory";
import { ENDPOINTS } from "~/repository/endpoints";
import type {
  AdminOrganization,
  AdminOrganizationAuditLog,
  AdminOrganizationBillingAccountUpdateInput,
  AdminOrganizationCommercialConfigResponse,
  AdminOrganizationCommercialConfigUpdateInput,
  AdminOrganizationCreateInput,
  AdminOrganizationMember,
  AdminOrganizationMemberCreateInput,
  AdminOrganizationMemberUpdateInput,
  AdminOrganizationPreferencesUpdateInput,
  AdminOrganizationRolloutChecklist,
  AdminOrganizationSupportNote,
  AdminOrganizationUpdateInput,
  AdminTeacherMigrationCandidate,
  AdminTeacherMigrationPreview,
} from "~/types/admin/organizations";
import type {
  OrganizationDomainRequest,
  OrganizationDomainRequestsResponse,
  OrganizationDomainUpdateInput,
  OrganizationDomainActivationInput,
  OrganizationBillingAccountResponse,
  OrganizationPreferencesResponse,
} from "~/types/organizations";

export default class AdminOrganizationsModule extends HttpFactory {
  private organizationUrl(template: string, organizationId: number): string {
    return template.replace("{organizationId}", organizationId.toString());
  }

  private memberUrl(organizationId: number, membershipId: number): string {
    return ENDPOINTS.ADMIN.ORGANIZATIONS.MEMBER_DETAIL.replace(
      "{organizationId}",
      organizationId.toString(),
    ).replace("{membershipId}", membershipId.toString());
  }

  private supportNoteUrl(organizationId: number, noteId: number): string {
    return ENDPOINTS.ADMIN.ORGANIZATIONS.SUPPORT_NOTE_DETAIL.replace(
      "{organizationId}",
      organizationId.toString(),
    ).replace("{noteId}", noteId.toString());
  }

  async list(
    fetchParams: FetchParams,
  ): Promise<PaginatedResponse<AdminOrganization>> {
    return await this.get<PaginatedResponse<AdminOrganization>>(
      ENDPOINTS.ADMIN.ORGANIZATIONS.SCHOOLS,
      fetchParams,
    );
  }

  async create(data: AdminOrganizationCreateInput): Promise<AdminOrganization> {
    return await this.post<AdminOrganization>(
      ENDPOINTS.ADMIN.ORGANIZATIONS.SCHOOLS,
      data,
    );
  }

  async update(
    organizationId: number,
    data: AdminOrganizationUpdateInput,
  ): Promise<AdminOrganization> {
    return await this.patch<AdminOrganization>(
      this.organizationUrl(
        ENDPOINTS.ADMIN.ORGANIZATIONS.DETAIL,
        organizationId,
      ),
      data,
    );
  }

  async listMembers(
    organizationId: number,
  ): Promise<AdminOrganizationMember[]> {
    return await this.get<AdminOrganizationMember[]>(
      this.organizationUrl(
        ENDPOINTS.ADMIN.ORGANIZATIONS.MEMBERS,
        organizationId,
      ),
    );
  }

  async createMember(
    organizationId: number,
    data: AdminOrganizationMemberCreateInput,
  ): Promise<AdminOrganizationMember> {
    return await this.post<AdminOrganizationMember>(
      this.organizationUrl(
        ENDPOINTS.ADMIN.ORGANIZATIONS.MEMBERS,
        organizationId,
      ),
      data,
    );
  }

  async updateMember(
    organizationId: number,
    membershipId: number,
    data: AdminOrganizationMemberUpdateInput,
  ): Promise<AdminOrganizationMember> {
    return await this.patch<AdminOrganizationMember>(
      this.memberUrl(organizationId, membershipId),
      data,
    );
  }

  async updateBillingAccount(
    organizationId: number,
    data: AdminOrganizationBillingAccountUpdateInput,
  ): Promise<OrganizationBillingAccountResponse> {
    return await this.patch<OrganizationBillingAccountResponse>(
      this.organizationUrl(
        ENDPOINTS.ADMIN.ORGANIZATIONS.BILLING_ACCOUNT,
        organizationId,
      ),
      data,
    );
  }

  async updateCommercialConfig(
    organizationId: number,
    data: AdminOrganizationCommercialConfigUpdateInput,
  ): Promise<AdminOrganizationCommercialConfigResponse> {
    return await this.patch<AdminOrganizationCommercialConfigResponse>(
      this.organizationUrl(
        ENDPOINTS.ADMIN.ORGANIZATIONS.COMMERCIAL_CONFIG,
        organizationId,
      ),
      data,
    );
  }

  async updatePreferences(
    organizationId: number,
    data: AdminOrganizationPreferencesUpdateInput | FormData,
  ): Promise<OrganizationPreferencesResponse> {
    return await this.patch<OrganizationPreferencesResponse>(
      this.organizationUrl(
        ENDPOINTS.ADMIN.ORGANIZATIONS.PREFERENCES,
        organizationId,
      ),
      data,
    );
  }

  async getPreferences(
    organizationId: number,
  ): Promise<OrganizationPreferencesResponse> {
    return await this.get(
      this.organizationUrl(
        ENDPOINTS.ADMIN.ORGANIZATIONS.PREFERENCES,
        organizationId,
      ),
    );
  }

  private domainRequestsUrl(organizationId: number): string {
    return this.organizationUrl(
      ENDPOINTS.ADMIN.ORGANIZATIONS.DOMAIN_REQUESTS,
      organizationId,
    );
  }

  async getDomainRequests(
    organizationId: number,
    purpose: "product" | "store" = "product",
  ): Promise<OrganizationDomainRequestsResponse> {
    return await this.get(
      this.domainRequestsUrl(organizationId),
      purpose === "store" ? { purpose } : {},
    );
  }

  async requestDomain(
    organizationId: number,
    domain: string,
    purpose: "product" | "store" = "product",
  ): Promise<OrganizationDomainRequest> {
    return await this.post(this.domainRequestsUrl(organizationId), {
      domain,
      ...(purpose === "store" ? { purpose } : {}),
    });
  }

  async updateDomainRequest(
    organizationId: number,
    id: number,
    input: OrganizationDomainUpdateInput,
  ): Promise<OrganizationDomainRequest> {
    return await this.patch(
      `${this.domainRequestsUrl(organizationId)}${id}/`,
      input,
    );
  }

  async changeDomainRequest(
    organizationId: number,
    id: number,
    action: "verify" | "cancel",
    expectedVersion: number,
  ): Promise<OrganizationDomainRequest> {
    return await this.post(
      `${this.domainRequestsUrl(organizationId)}${id}/${action}/`,
      { expected_version: expectedVersion },
    );
  }

  async activateDomain(
    organizationId: number,
    id: number,
    input: OrganizationDomainActivationInput,
  ): Promise<OrganizationDomainRequest> {
    return await this.post(
      `${this.domainRequestsUrl(organizationId)}${id}/activate/`,
      input,
    );
  }

  async deactivateDomain(
    organizationId: number,
    id: number,
    expectedVersion: number,
    reason: string,
  ): Promise<OrganizationDomainRequest> {
    return await this.post(
      `${this.domainRequestsUrl(organizationId)}${id}/deactivate/`,
      { expected_version: expectedVersion, reason },
    );
  }

  async listAuditLog(
    organizationId: number,
  ): Promise<AdminOrganizationAuditLog[]> {
    return await this.get<AdminOrganizationAuditLog[]>(
      this.organizationUrl(
        ENDPOINTS.ADMIN.ORGANIZATIONS.AUDIT_LOG,
        organizationId,
      ),
    );
  }

  async listSupportNotes(
    organizationId: number,
  ): Promise<AdminOrganizationSupportNote[]> {
    return await this.get<AdminOrganizationSupportNote[]>(
      this.organizationUrl(
        ENDPOINTS.ADMIN.ORGANIZATIONS.SUPPORT_NOTES,
        organizationId,
      ),
    );
  }

  async createSupportNote(
    organizationId: number,
    body: string,
  ): Promise<AdminOrganizationSupportNote> {
    return await this.post<AdminOrganizationSupportNote>(
      this.organizationUrl(
        ENDPOINTS.ADMIN.ORGANIZATIONS.SUPPORT_NOTES,
        organizationId,
      ),
      { body },
    );
  }

  async updateSupportNote(
    organizationId: number,
    noteId: number,
    body: string,
  ): Promise<AdminOrganizationSupportNote> {
    return await this.patch<AdminOrganizationSupportNote>(
      this.supportNoteUrl(organizationId, noteId),
      { body },
    );
  }

  async getRolloutChecklist(
    organizationId: number,
  ): Promise<AdminOrganizationRolloutChecklist> {
    return await this.get<AdminOrganizationRolloutChecklist>(
      this.organizationUrl(
        ENDPOINTS.ADMIN.ORGANIZATIONS.ROLLOUT_CHECKLIST,
        organizationId,
      ),
    );
  }

  async updateRolloutChecklist(
    organizationId: number,
    data: Partial<AdminOrganizationRolloutChecklist>,
  ): Promise<AdminOrganizationRolloutChecklist> {
    return await this.patch<AdminOrganizationRolloutChecklist>(
      this.organizationUrl(
        ENDPOINTS.ADMIN.ORGANIZATIONS.ROLLOUT_CHECKLIST,
        organizationId,
      ),
      data,
    );
  }

  async listTeacherMigrationCandidates(
    organizationId: number,
    email: string,
  ): Promise<AdminTeacherMigrationCandidate[]> {
    return await this.get<AdminTeacherMigrationCandidate[]>(
      this.organizationUrl(
        ENDPOINTS.ADMIN.ORGANIZATIONS.TEACHER_MIGRATION_CANDIDATES,
        organizationId,
      ),
      { email },
    );
  }

  async previewTeacherMigration(
    organizationId: number,
    userId: number,
  ): Promise<AdminTeacherMigrationPreview> {
    return await this.post<AdminTeacherMigrationPreview>(
      this.organizationUrl(
        ENDPOINTS.ADMIN.ORGANIZATIONS.TEACHER_MIGRATION_PREVIEW,
        organizationId,
      ),
      { user_id: userId },
    );
  }

  async executeTeacherMigration(
    organizationId: number,
    data: {
      user_id: number;
      role: "owner" | "manager" | "teacher";
      teaching_enabled: boolean;
    },
  ): Promise<AdminTeacherMigrationPreview> {
    return await this.post<AdminTeacherMigrationPreview>(
      this.organizationUrl(
        ENDPOINTS.ADMIN.ORGANIZATIONS.TEACHER_MIGRATIONS,
        organizationId,
      ),
      data,
    );
  }

  async exportCsv(organizationId: number): Promise<Blob> {
    return await this.get<Blob>(
      this.organizationUrl(
        ENDPOINTS.ADMIN.ORGANIZATIONS.EXPORTS,
        organizationId,
      ),
      {},
      { responseType: "blob" },
    );
  }
}
